import { useState, useRef, useEffect } from 'react';
import { useOnboarding } from '../OnboardingContext';
import { Mail, Loader2, AlertTriangle, ArrowRight, CheckCircle2, Building2 } from 'lucide-react';

import { useToast } from '@/hooks/use-toast';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

// ─── DEV TEST MODE ────────────────────────────────────────────────────────────
// Set to true to bypass real OTP verification during testing.
// Code 000000 skips the API call. Remove before production.
const TEST_MODE = false;
const TEST_EMAIL = '';
const TEST_BYPASS_CODE = '000000';
// ──────────────────────────────────────────────────────────────────────────────

// Helper to check if email is a placeholder (used for auto-save before real email is captured)
const isPlaceholderEmail = (email: string) => {
  if (!email) return true;
  return email.includes('@pending.local') || 
         email.includes('@temp.local') || 
         email.startsWith('pending_') ||
         email.startsWith('corp_');
};

export function EmailStep() {
  const { data, updateData, goToNextStep, setCustomBackHandler, setCurrentStep, currentStep } = useOnboarding();
  const { toast } = useToast();
  // Don't pre-fill with placeholder emails - user should always enter their own
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Consent checkboxes
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  // OTP verification state
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [existingApplication, setExistingApplication] = useState(false);
  const [existingAppIsClient, setExistingAppIsClient] = useState(false);
  const [existingAppEmail, setExistingAppEmail] = useState<string | null>(null);
  const [existingAppAction, setExistingAppAction] = useState<'login' | 'resume'>('login');
  // CR 3043425299 — invited team members must be routed into the invite-acceptance flow,
  // not a fresh self-owned corporate onboarding.
  const [pendingInvite, setPendingInvite] = useState<{ email: string; company_name: string } | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // TEST MODE: auto-fill test email on mount
  useEffect(() => {
    if (TEST_MODE && !email) {
      setEmail(TEST_EMAIL);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Custom back handler for OTP view
  useEffect(() => {
    if (codeSent) {
      setCustomBackHandler(() => {
        setCodeSent(false);
        setCode(['', '', '', '', '', '']);
        setOtpError('');
        return true;
      });
    } else {
      setCustomBackHandler(null);
    }
    
    return () => setCustomBackHandler(null);
  }, [codeSent, setCustomBackHandler]);

  // Focus first OTP input when code sent
  useEffect(() => {
    if (codeSent) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [codeSent]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Check for existing application when email changes
  // Checks within the SAME account type (individual vs corporate)
  // Also checks by extracted name to catch typos in email
  const checkExistingApplication = async (emailToCheck: string) => {
    if (!validateEmail(emailToCheck)) {
      setExistingApplication(false);
      return;
    }

    setIsCheckingEmail(true);
    try {
      const currentAccountType = data.accountType || 'individual';
      const extractedFirst = data.extractedFirstName || data.firstName;
      const extractedLast = data.extractedLastName || data.lastName;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const resp = await fetch(`${supabaseUrl}/functions/v1/onboarding-session-lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({
          action: 'check_email',
          email: emailToCheck,
          account_type: currentAccountType,
          extracted_first: extractedFirst,
          extracted_last: extractedLast,
        }),
      });

      if (!resp.ok) {
        setExistingApplication(false);
        return;
      }

      const lookupResult = await resp.json();
      const existingSession = lookupResult?.session;

      const ADVANCED_STEPS = [
        'passcode', 'document-upload', 'document-details-confirmation',
        'individual-declarations', 'employment', 'income', 'payment-destinations',
        'document-signing', 'final-declarations', 'success',
        'corporate-passcode', 'corporate-business-activity', 'corporate-services',
        'corporate-payment-flows', 'corporate-directors-shareholders', 'corporate-ubo-trace',
        'corporate-documents', 'corporate-document-signing', 'corporate-success'
      ];

      if (existingSession && ADVANCED_STEPS.includes(existingSession.current_step)) {
        setExistingApplication(true);
        return;
      }

      if (lookupResult?.name_match) {
        setExistingApplication(true);
        return;
      }

      setExistingApplication(false);
    } catch (err) {
      console.error('Error checking for existing application:', err);
      setExistingApplication(false);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // Debounced email check
  useEffect(() => {
    if (checkTimeoutRef.current) {
      clearTimeout(checkTimeoutRef.current);
    }
    
    if (validateEmail(email) && !codeSent) {
      checkTimeoutRef.current = setTimeout(() => {
        checkExistingApplication(email);
      }, 500); // 500ms debounce
    } else {
      setExistingApplication(false);
    }
    
    return () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [email, codeSent]);

  const sendingRef = useRef(false);

  const handleSendCode = async () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Guard against double invocations (React strict mode, double-click, etc.)
    if (sendingRef.current) return;
    sendingRef.current = true;

    // TEST MODE: bypass real OTP — just mark as sent
    if (TEST_MODE) {
      updateData({ email: email.toLowerCase(), dataProcessingConsent: true, marketingConsent });
      setCodeSent(true);
      setResendTimer(30);
      toast({ title: '🧪 Test Mode', description: `Use code ${TEST_BYPASS_CODE} to verify` });
      sendingRef.current = false;
      return;
    }
    setEmailError('');
    setExistingApplication(false);
    setPendingInvite(null);

    // ── CR 3043425299 — Invite guard ──────────────────────────────────────────
    // If this email has a live corporate invitation, route to the acceptance
    // flow (/app/join) instead of starting a new self-owned corporate onboarding.
    // This closes the linkage-loss loophole behind defect 3043309093, where an
    // invitee whose phone verification failed on /app/join returned via the
    // generic onboarding entry and started a fresh business onboarding.
    try {
      const inviteLookup = await supabase.functions.invoke('corporate-member-join', {
        body: { action: 'lookup_invitation', email: email.toLowerCase() },
      });
      const inv = (inviteLookup?.data as any)?.invitation;
      if (inv && inv.email && inv.company_name) {
        setPendingInvite({ email: inv.email, company_name: inv.company_name });
        toast({
          title: 'Team invitation found',
          description: `You have a pending invite to join ${inv.company_name}. Please accept it instead.`,
        });
        sendingRef.current = false;
        setIsSendingCode(false);
        return;
      }
    } catch (e) {
      // Non-blocking — if the invite lookup itself fails, fall through to normal onboarding.
      console.warn('[EmailStep] invite lookup failed (non-blocking):', e);
    }

    try {
      // Build phone number for session lookup (to update existing session rather than create new)
      const fullPhoneNumber = data.countryCode && data.phoneNumber 
        ? `${data.countryCode}${data.phoneNumber}` 
        : undefined;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const resp = await fetch(`${supabaseUrl}/functions/v1/send-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({ 
          email: email.toLowerCase(),
          accountType: data.accountType || 'individual',
          phoneNumber: fullPhoneNumber
        }),
      });

      let response: any = {};
      try {
        response = await resp.json();
      } catch {
        // JSON parse failed
      }

      if (!resp.ok || response?.error) {
        // Check if it's an existing client / duplicate email (409)
        if (resp.status === 409 || response?.existing_application || response?.is_existing_client) {
          setExistingApplication(true);
          setExistingAppIsClient(!!response?.is_existing_client);
          setExistingAppEmail(response?.email || null);
          setExistingAppAction(response?.action === 'resume' ? 'resume' : 'login');
          setEmailError(response?.error || 'This email is already registered.');
          return;
        }
        
        const errorMessage = response?.error || 'Failed to send verification code';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive'
        });
        return;
      }

      // Save consent preferences
      updateData({ 
        email: email.toLowerCase(),
        dataProcessingConsent: true,
        marketingConsent: marketingConsent
      });
      setCodeSent(true);
      setResendTimer(30);
      toast({
        title: 'Code sent',
        description: 'Check your email for the verification code'
      });
    } catch (err) {
      console.error('Error sending code:', err);
      toast({
        title: 'Error',
        description: 'Failed to send verification code',
        variant: 'destructive'
      });
    } finally {
      setIsSendingCode(false);
      sendingRef.current = false;
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setOtpError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) return;

    // TEST MODE: code 000000 bypasses real verification
    if (TEST_MODE && fullCode === TEST_BYPASS_CODE) {
      toast({ title: '🧪 Test Mode', description: 'Email bypass accepted' });
      // Mark email as verified so skip logic works correctly
      updateData({ emailOtpVerified: true });
      if (currentStep === 'corporate-email') {
        setCurrentStep('corporate-passcode');
      } else {
        setCurrentStep('passcode');
      }
      return;
    }

    setIsVerifying(true);
    setOtpError('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const resp = await fetch(`${supabaseUrl}/functions/v1/verify-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey,
        },
        body: JSON.stringify({ email: email.toLowerCase(), otp: fullCode, accountType: data.accountType || 'individual' }),
      });

      const response = await resp.json();

      if (!resp.ok || response?.error) {
        setOtpError(response?.error || 'Invalid code. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        setIsVerifying(false);
        return;
      }

      toast({
        title: 'Success',
        description: 'Email verified successfully'
      });
      
      // Reset verifying state first
      setIsVerifying(false);
      // Mark email as verified so skip logic works correctly
      updateData({ emailOtpVerified: true });
      
      // Navigate to next step
      if (currentStep === 'corporate-email') {
        setCurrentStep('corporate-passcode');
      } else {
        setCurrentStep('passcode');
      }
    } catch (err) {
      console.error('Error verifying code:', err);
      setOtpError('Failed to verify code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setCode(['', '', '', '', '', '']);
    setOtpError('');
    await handleSendCode();
  };

  const isCodeComplete = code.every(digit => digit !== '');

  // ── Already-verified bypass ─────────────────────────────────────────────────
  const alreadyVerified = Boolean(data.emailOtpVerified && data.email && !isPlaceholderEmail(data.email));

  // Auto-advance immediately — never show this step again once verified
  useEffect(() => {
    if (alreadyVerified) {
      goToNextStep();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (alreadyVerified) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-24 pb-8 px-4 sm:px-6">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
          <div className="mb-6">
            <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
              <Mail className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Email</h1>
            <p className="text-muted-foreground">
              Enter your email address for account verification and important notifications.
            </p>
          </div>

          {/* Verified state card */}
          <div className="rounded-2xl border-2 border-accent/40 bg-accent/5 p-5 flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-0.5">Email Verified</p>
              <p className="text-base font-semibold text-foreground truncate">{data.email}</p>
            </div>
          </div>

          <button
            onClick={goToNextStep}
            className="w-full h-14 rounded-full bg-accent text-accent-foreground font-semibold flex items-center justify-center gap-2 hover:bg-accent/90 transition-all mt-auto"
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col pt-24 pb-8 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full overflow-y-auto overflow-x-hidden">
        <div className="mb-6">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6">
            <Mail className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Email
          </h1>
          <p className="text-muted-foreground">
            Enter your email address for account verification and important notifications.
          </p>
        </div>

        {/* Email input row */}
        <div className="space-y-2 mb-4">
          <label className="text-sm font-medium text-foreground">Email Address</label>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError('');
                  setExistingApplication(false);
                }}
                placeholder="your@email.com"
                className="input-field w-full pr-10"
                autoFocus={!codeSent}
                disabled={codeSent || isSendingCode}
              />
              <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            </div>

            {codeSent ? (
              <button
                onClick={() => {
                  setCodeSent(false);
                  setCode(['', '', '', '', '', '']);
                  setOtpError('');
                  setEmailError('');
                  setResendTimer(0);
                }}
                className="px-4 py-3 text-sm font-medium rounded-xl border-2 border-border text-foreground hover:bg-secondary transition-colors whitespace-nowrap w-full md:w-auto flex items-center justify-center gap-2 min-w-[120px]"
              >
                Edit Email
              </button>
            ) : (
              <button
                onClick={handleSendCode}
                disabled={!email || isSendingCode || existingApplication || isCheckingEmail || !!pendingInvite}
                className="px-4 py-3 text-sm font-medium rounded-xl border-2 border-accent text-accent hover:bg-accent/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap w-full md:w-auto flex items-center justify-center gap-2 min-w-[120px]"
              >
                {isSendingCode ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : pendingInvite ? (
                  'Accept invitation'
                ) : existingApplication ? (
                  'Email in use'
                ) : (
                  'Get Code'
                )}
              </button>
            )}
          </div>
          {emailError && (
            <p className="text-sm text-destructive">{emailError}</p>
          )}
        </div>

        {/* CR 3043425299 — Pending team invitation detected */}
        {pendingInvite && (
          <div className="rounded-2xl border-2 border-accent bg-accent/10 p-5 space-y-4 animate-fade-in-up mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">You've been invited to a team</h3>
                <p className="text-sm text-muted-foreground">
                  {pendingInvite.company_name} has invited <span className="text-foreground">{pendingInvite.email}</span> to join as a team member.
                  Please accept the invitation instead of starting a new business account.
                </p>
              </div>
            </div>
            <Button
              onClick={() => {
                window.location.href = `/app/join?email=${encodeURIComponent(pendingInvite.email)}`;
              }}
              className="w-full flex items-center justify-center gap-2"
            >
              Accept invitation <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              If this isn't you, use a different email address.
            </p>
          </div>
        )}

        {/* Existing application / duplicate email warning */}
        {existingApplication && (
          <div className="rounded-2xl border-2 border-warning bg-warning/10 p-5 space-y-4 animate-fade-in-up mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">
                  {existingAppIsClient ? 'Email Already Registered' : 'Application Already Exists'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {emailError || (existingAppIsClient
                    ? 'This email address is already associated with an existing account.'
                    : 'An application with this email address is already in progress.')}
                  {existingAppEmail && existingAppEmail !== 'null' && (
                    <span className="block mt-1 text-foreground">
                      Account: {existingAppEmail}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  window.location.href = existingAppAction === 'resume' ? '/app/login?resume=1' : '/app/login';
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-all"
              >
                {existingAppAction === 'resume' ? 'Resume Existing Application' : 'Log in to your account'}
                <ArrowRight className="w-4 h-4" />
              </button>
              
              {/* Only allow "use different email" for non-client duplicates (e.g. in-progress apps).
                  Existing approved clients must log in — no bypass allowed. */}
              {!existingAppIsClient && (
              <button
                onClick={() => {
                  setExistingApplication(false);
                  setExistingAppIsClient(false);
                  setExistingAppEmail(null);
                  setEmailError('');
                  setEmail('');
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-border text-foreground font-medium hover:bg-secondary transition-all text-sm"
              >
                Use a different email
              </button>
              )}
            </div>
            
            <p className="text-xs text-center text-muted-foreground">
              {existingAppIsClient
                ? 'You already have an account with us. Please log in to access your account. If you need assistance, contact our support team.'
                : existingAppAction === 'login'
                ? 'If this is your account, please log in. Otherwise, use a different email address.'
                : "You'll be asked to verify your identity to resume your application."}
            </p>
          </div>
        )}

        {/* OTP verification section */}
        <div className="rounded-2xl border border-border bg-card/50 p-4 sm:p-6 space-y-4">
          <p className="text-sm text-foreground">
            Enter the 6-digit code sent to your email
          </p>
          
          <div className="flex flex-col gap-4">
            <div className="flex gap-2 justify-center flex-shrink-0">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={!codeSent || isVerifying}
                  className={`w-10 h-12 sm:w-11 sm:h-14 text-center text-xl font-semibold border-2 rounded-xl focus:outline-none ${
                    codeSent && !isVerifying
                      ? 'border-border bg-background focus:border-accent' 
                      : 'border-border/50 bg-muted/30 text-muted-foreground cursor-not-allowed'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={handleVerify}
              disabled={!isCodeComplete || !codeSent || isVerifying}
              className="w-full h-12 text-sm font-semibold rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </button>
          </div>

          {otpError && (
            <p className="text-destructive text-sm">{otpError}</p>
          )}

          {codeSent && (
            <div className="flex items-center justify-end text-sm">
              {resendTimer > 0 ? (
                <span className="text-muted-foreground">Resend in {resendTimer}s</span>
              ) : (
                <button 
                  onClick={handleResend} 
                  disabled={isSendingCode}
                  className="text-accent font-medium hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
